/**
 * ProtectedRoute Component
 * 
 * Wrapper component that protects routes based on authentication and role.
 * Redirects unauthenticated users to /auth and users with wrong roles to their home screen.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOutletAuth } from '../lib/auth/OutletAuthProvider';
import type { DeviceRole } from '../lib/auth/storage';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: DeviceRole | DeviceRole[];
  redirectTo?: string;
}

/**
 * Get the home route for a given role
 */
function getRoleHomeRoute(role: DeviceRole): string {
  switch (role) {
    case 'customer':
      return '/';
    case 'capture':
      return '/capture';
    case 'cashier':
      return '/cashier';
    default:
      return '/';
  }
}

/**
 * Check if a role is allowed
 */
function isRoleAllowed(userRole: DeviceRole, requiredRole: DeviceRole | DeviceRole[]): boolean {
  if (Array.isArray(requiredRole)) {
    return requiredRole.includes(userRole);
  }
  return userRole === requiredRole;
}

/**
 * ProtectedRoute component
 * 
 * Protects routes by checking:
 * 1. Authentication status - redirects to /auth if not authenticated
 * 2. Role permissions - redirects to role home if wrong role
 */
export function ProtectedRoute({ 
  children, 
  requiredRole, 
  redirectTo 
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, authState } = useOutletAuth();
  const location = useLocation();

  // Show nothing while loading (provider shows loading screen)
  if (isLoading) {
    return null;
  }

  // Not authenticated - redirect to auth screen
  if (!isAuthenticated || !authState) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // No role requirement - just need to be authenticated
  if (!requiredRole) {
    return <>{children}</>;
  }

  // Check role permissions
  const hasPermission = isRoleAllowed(authState.role, requiredRole);

  if (!hasPermission) {
    // Wrong role - redirect to role-specific home or custom redirect
    const destination = redirectTo || getRoleHomeRoute(authState.role);
    return <Navigate to={destination} replace />;
  }

  // All checks passed - render children
  return <>{children}</>;
}
